/**
 * React hook for API access
 * Provides authenticated API client methods
 */

import { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthProvider";
import { useToast } from "../components/Toast";
import { API_BASE } from "../config";
import {
  ApiClient,
  reportPaymentRequiredResponse,
  type PaymentRequiredKey,
  type RequestOptions,
  type TokenGetter,
} from "../lib/api-client";

export type { RequestOptions, ApiError } from "../lib/api-client";

/**
 * Hook that provides an authenticated API client
 */
export function useApi() {
  const { getToken: authGetToken, ensureToken, accessToken, forceRefresh } = useAuth();
  const toast = useToast();
  const { t } = useTranslation();

  // Global 402 (entitlement gate) → toast. Ref keeps the callback identity
  // stable so the memoized client isn't recreated when the toast context or
  // translations re-render.
  const on402Ref = useRef<(key: PaymentRequiredKey) => void>(() => {});
  on402Ref.current = (key) => toast.error(t(`settings.${key}`));
  const on402 = useCallback((key: PaymentRequiredKey) => on402Ref.current(key), []);

  // Ref to hold the latest accessToken for the rare fallback path,
  // so that the getToken callback identity stays stable across token refreshes.
  const accessTokenRef = useRef(accessToken);
  accessTokenRef.current = accessToken;

  // Create a stable token getter (only depends on stable function refs)
  const getToken: TokenGetter = useCallback(async () => {
    // Use getToken if available (new API)
    if (typeof authGetToken === "function") {
      return authGetToken();
    }

    // Fall back to ensureToken
    if (typeof ensureToken === "function") {
      try {
        const token = await ensureToken();
        if (token) return token;
      } catch {
        // Fall through to accessToken
      }
    }

    // Fall back to cached accessToken (via ref to avoid dependency churn)
    return accessTokenRef.current ?? null;
  }, [authGetToken, ensureToken]);

  // Create API client instance (memoized)
  const client = useMemo(
    () => new ApiClient({ baseUrl: API_BASE, getToken, onTokenExpired: forceRefresh, on402 }),
    [getToken, forceRefresh, on402]
  );

  // Expose methods
  return useMemo(
    () => ({
      get: <T,>(path: string, options?: RequestOptions) =>
        client.get<T>(path, options),
      post: <T,>(path: string, body?: unknown, options?: RequestOptions) =>
        client.post<T>(path, body, options),
      put: <T,>(path: string, body?: unknown, options?: RequestOptions) =>
        client.put<T>(path, body, options),
      patch: <T,>(path: string, body?: unknown, options?: RequestOptions) =>
        client.patch<T>(path, body, options),
      delete: <T,>(path: string, options?: RequestOptions) =>
        client.delete<T>(path, options),
      fetch: (path: string, init?: RequestInit, options?: RequestOptions) =>
        client.fetch(path, init, options),

      // Direct client access (for advanced use cases)
      client,

      // Token getter (for components that need raw token)
      getToken,
    }),
    [client, getToken]
  );
}

/**
 * Hook that provides a simple fetch function with auth
 * For components that just need basic authed fetch
 */
export function useAuthedFetch() {
  const { getToken, client } = useApi();

  return useCallback(
    async (input: string, init?: RequestInit): Promise<Response> => {
      // Determine if this is a full URL or a path
      const isFullUrl = input.startsWith("http://") || input.startsWith("https://");

      if (isFullUrl) {
        // For full URLs, just add auth header manually
        const token = await getToken();
        const headers = new Headers(init?.headers);
        if (token) {
          headers.set("Authorization", `Bearer ${token}`);
        }
        const response = await fetch(input, { ...init, headers });
        // This branch bypasses ApiClient (and its on402 wiring), but session
        // save/upload — the endpoints that return {"error":"session_limit"} —
        // come through here (SessionPanel passes full getApiBaseUrl() URLs).
        // Surface 402s via the global handler so the entitlement toast fires.
        await reportPaymentRequiredResponse(response);
        return response;
      }

      // For paths, use the client
      return client.fetch(input, init);
    },
    [getToken, client]
  );
}

